import type { SvgIconComponent } from "@mui/icons-material";
import { Collapse, IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import type { PropsWithChildren } from "react";
import CollapseIcon from "@entrilia/shared/icons/CollapseIcon";
import ExpandIcon from "@entrilia/shared/icons/ExpandIcon";
import useToggleState from "@entrilia/shared/hooks/useToggleState";
import { useMainMenuContext } from "../leftSideMenu/MainMenuContext";

interface Props {
  id?: string;
  title: string;
  Icon?: SvgIconComponent;
}

const MenuGroupItem = ({ id, title, Icon, children }: PropsWithChildren<Props>) => {
  const { persistedClosedItems, addClosedItem, removeClosedItem } = useMainMenuContext();

  const [opened, toggleOpened] = useToggleState(!persistedClosedItems.includes(title));

  const handleToggleClick = () => {
    const wasOpened = opened;
    toggleOpened();
    if (wasOpened) {
      addClosedItem(title);
    } else {
      removeClosedItem(title);
    }
  };

  return (
    <>
      <ListItem
        id={id}
        onClick={handleToggleClick}
        disablePadding
        secondaryAction={
          <IconButton edge="end" aria-label="navigate" sx={(t) => ({ color: t.palette.action.active })}>
            {opened ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        }
      >
        <ListItemButton sx={{ py: 1, px: 2 }}>
          {Icon && (
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Icon />
            </ListItemIcon>
          )}
          <ListItemText sx={{ m: 0 }} slotProps={{ primary: { noWrap: true } }}>
            {title}
          </ListItemText>
        </ListItemButton>
      </ListItem>
      <Collapse sx={{ pl: 4 }} in={opened} timeout="auto" unmountOnExit>
        {children}
      </Collapse>
    </>
  );
};

export default MenuGroupItem;
